import {
  THours,
  ITmDesignCenterDetailsCSProps,
} from '@/components/templates/tm-design-center-detail/tm-design-center-details.types';
import {
  getFooterRes,
  getAllEntries,
  getHeaderRes,
  getBannerRes,
  getLocationsRes,
  getGlobalLinksRes,
  doesDesignStudioSupport3D,
  getEntriesWithFilter,
  getDesignStudiosDataFromJSON,
} from '@/lib/contentstack-sdk';
import { cache } from 'react';

import {
  IOrNavBarBlockProps,
  TFooterProps,
  TPage,
  TBannerProps,
  ICustomUrls,
  IDesignStudiosJSON,
} from '@/lib/contentstack-sdk/types';
import { IDesignStudio } from '../organisms/or-design-studio-block/or-design-studio-block.types';
import { IStudioHit } from '@/lib/search/types';

export interface IDesignStudioCSProps {
  pageUrl: string;
  hours: THours[];
  CsAddress: string;
  CsName: string;
  CsCity: string;
  CsState: string;
  lat: number;
  long: number;
  directionsLink: string;
  CsPhoneNumber: string;
  CsZip: string;
  supports3D: boolean;
  is_virtual: boolean;
}

export interface ILocationData {
  name: string;
  country: string;
  state_name: string;
  county_name: string;
  county_id: number;
  state_abbrev: string;
  zip: string[];
  studio_id: number;
}
export const getAllStudios = async (
  studio3DData: IDesignStudiosJSON,
): Promise<IDesignStudio[]> => {
  let studiosFromAlgolia: IStudioHit[] = [];
  try {
    const studiosFromAlgoliaResponse = await fetch(
      `${process.env.NEXT_PUBLIC_API_BASE_URL}/algolia/design-studios?design_studio_id=all`,
      {
        method: 'GET',
        headers: { Accept: 'application/json' },
        next: { revalidate: 3600 },
      },
    );
    studiosFromAlgolia =
      (await studiosFromAlgoliaResponse.json()) as IStudioHit[];
  } catch (err) {
    console.error('Failed to fetch Algolia studios:', err);
    return [];
  }

  let csStudios: ITmDesignCenterDetailsCSProps[] = [];
  try {
    csStudios = (await getEntriesWithFilter({
      contentTypeUid: 'tm_design_studio',
      filters: {},
    })) as ITmDesignCenterDetailsCSProps[];
  } catch (err) {
    console.error('Failed to fetch Contentstack studios:', err);
  }

  const csStudioMap = new Map<number, ITmDesignCenterDetailsCSProps>();
  for (const csStudio of csStudios) {
    if (csStudio.studio_id) {
      csStudioMap.set(Number(csStudio.studio_id.location[0]), csStudio);
    }
  }

  const mergedStudioData = await Promise.all(
    studiosFromAlgolia.map(async (studio) => {
      let supports3D = false;
      try {
        supports3D = await doesDesignStudioSupport3D(studio.Id, studio3DData);
      } catch (err) {
        console.error(
          `Failed to check 3D support for studio ${studio.Id}:`,
          err,
        );
      }

      const csStudio = csStudioMap.get(studio.Id);

      if (!csStudio) {
        return {
          ...studio,
          supports3D,
          hours: [],
          pageUrl: '',
          CsAddress: '',
          CsName: '',
          CsState: '',
          lat: -1,
          long: -1,
          CsCity: '',
          CsZip: '',
          is_virtual: true,
          directionsLink: '',
          CsPhoneNumber: '',
        };
      }

      return {
        ...studio,
        supports3D,
        hours: csStudio.hours,
        lat: csStudio.geocoding?.lat || -1,
        long: csStudio.geocoding?.long || -1,
        pageUrl: csStudio.page_url,
        CsAddress: csStudio.design_studio_address || '',
        CsName: csStudio.design_studio_name,
        CsCity: csStudio.design_studio_city || '',
        CsZip: csStudio.design_studio_zip || '',
        CsState: csStudio.design_studio_state || '',
        directionsLink: csStudio.directions_link || undefined,
        CsPhoneNumber: csStudio.phone_number,
        is_virtual: csStudio.is_virtual || studio.IsDigital,
      };
    }),
  );

  return mergedStudioData;
};

export const fetchInitialProps = async (): Promise<{
  header: IOrNavBarBlockProps;
  footer: TFooterProps;
  entries: TPage[];
  banner: TBannerProps;
  locationData: ILocationData[];
  studioData: IDesignStudio[];
  globalLinks: ICustomUrls[];
  studio3DData: IDesignStudiosJSON;
}> => {
  let studio3DData: IDesignStudiosJSON = { design_studios: [] };
  try {
    studio3DData = await getDesignStudiosDataFromJSON();
  } catch (err) {
    console.error('Failed to fetch studio 3D data:', err);
  }

  let footer: TFooterProps = {} as TFooterProps;
  let entries: TPage[] = [];
  let header: IOrNavBarBlockProps = {} as IOrNavBarBlockProps;
  let banner: TBannerProps = {} as TBannerProps;
  let locationData: ILocationData[] = [];
  let studioData: IDesignStudio[] = [];
  let globalLinks: ICustomUrls[] = [];

  try {
    [footer, entries, header, banner, locationData, studioData, globalLinks] =
      await Promise.all([
        getFooterRes().catch((err) => {
          console.error('Failed to fetch footer:', err);
          return {} as TFooterProps;
        }),
        getAllEntries().catch((err) => {
          console.error('Failed to fetch entries:', err);
          return [];
        }),
        getHeaderRes().catch((err) => {
          console.error('Failed to fetch header:', err);
          return {} as IOrNavBarBlockProps;
        }),
        getBannerRes().catch((err) => {
          console.error('Failed to fetch banner:', err);
          return {} as TBannerProps;
        }),
        getLocationsRes().catch((err) => {
          console.error('Failed to fetch location data:', err);
          return [];
        }),
        getAllStudios(studio3DData).catch((err) => {
          console.error('Failed to fetch studio data:', err);
          return [];
        }),
        getGlobalLinksRes().catch((err) => {
          console.error('Failed to fetch global links:', err);
          return [];
        }),
      ]);
  } catch (err) {
    console.error('Failed to fetch initial props:', err);
  }

  return {
    entries,
    footer,
    header,
    banner,
    locationData,
    studioData,
    globalLinks,
    studio3DData,
  };
};

export const getCachedInitialProps = cache(async () => {
  try {
    return await fetchInitialProps();
  } catch (err) {
    console.error('Failed to get cached initial props:', err);
    return {
      entries: [],
      footer: {} as TFooterProps,
      header: {} as IOrNavBarBlockProps,
      banner: {} as TBannerProps,
      locationData: [],
      studioData: [],
      globalLinks: [],
      studio3DData: { design_studios: [] },
    };
  }
});
